import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Upload, Beaker, CheckCircle2, AlertCircle, Loader2, FileSpreadsheet,
  Info, Trash2, Eye, ChevronDown, ChevronUp,
} from "lucide-react";
import api, { formatErr } from "../lib/api";

const SIMULATORS = [
  { key: "hysys",       name: "Aspen HYSYS",       hint: "File → Export → Spreadsheet" },
  { key: "aspen_plus",  name: "Aspen Plus",        hint: "File → Export → Report to Excel" },
  { key: "pro_ii",      name: "PRO/II (AVEVA)",    hint: "Reports → Stream Summary → Export" },
  { key: "unisim",      name: "UniSim (Honeywell)", hint: "File → Export → Workbook" },
  { key: "chemcad",     name: "CHEMCAD",           hint: "Reports → Stream Report → Export" },
  { key: "dwsim",       name: "DWSIM",             hint: "File → Save As JSON (recommended)" },
  { key: "generic",     name: "Generic Excel/CSV", hint: "Any H&MB spreadsheet works — use column mapping if auto-detect fails" },
];

export default function SimulationImportPanel({ projectId }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [previewRows, setPreviewRows] = useState({ streams: false, equipment: false });
  const inputRef = useRef(null);

  const loadPreview = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get(
        `/projects/${projectId}/simulation/import/preview`);
      setPreview(data);
    } catch (e) {
      if (e?.response?.status !== 404) setErr(formatErr(e.response?.data?.detail) || e.message);
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setErr("");
    setOk("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/projects/${projectId}/simulation/import/upload`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setOk(`Parsed ${data.streams_count} streams, ${data.equipment_count} equipment via ${data.simulator} (${data.confidence_pct}% confidence)`);
      await loadPreview();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleCommit = async () => {
    setCommitting(true);
    setErr("");
    setOk("");
    try {
      const { data } = await api.post(
        `/projects/${projectId}/simulation/import/commit`, {});
      setOk(`✓ Committed to PDO — ${data.streams_written} streams, ${data.equipment_written} equipment. B.1 status: ${data.b1_status?.status}. You can now proceed to B.2 (PFD), B.3 (P&ID), B.4 (Datasheets).`);
      await loadPreview();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setCommitting(false);
    }
  };

  const handleClear = async () => {
    setLoading(true);
    setErr("");
    try {
      await api.delete(`/projects/${projectId}/simulation/import/preview`);
      setPreview(null);
      setOk("Preview cleared.");
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const result = preview?.result;
  const completeness = result?.completeness || {};

  return (
    <div className="space-y-4" data-testid="simulation-import-panel">
      {/* Header */}
      <div className="rounded-lg border border-cyan-900/40 bg-cyan-950/20 p-4">
        <div className="flex items-start gap-3">
          <Beaker className="h-5 w-5 text-cyan-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-cyan-100">
              Import external simulator output
            </div>
            <p className="text-xs text-slate-400 mt-1">
              Skip re-running the internal solver — upload your HYSYS / Aspen /
              PRO-II / UniSim / CHEMCAD / DWSIM / Excel case output and Capexara
              will populate streams, equipment and utility summary automatically.
              PFD, P&amp;ID, datasheets and hydraulic sizing then proceed from your imported data.
            </p>
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-2 text-xs text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1"
              data-testid="toggle-supported-formats"
            >
              <Info className="h-3.5 w-3.5" />
              {expanded ? "Hide" : "Show"} supported formats & export instructions
              {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
            {expanded && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {SIMULATORS.map((s) => (
                  <div key={s.key} className="rounded border border-slate-800 bg-slate-950/50 p-2">
                    <div className="text-xs font-semibold text-slate-200">{s.name}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{s.hint}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upload */}
      <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-4">
        <div className="text-xs font-semibold text-slate-300 mb-2 uppercase tracking-wide">
          1 · Upload simulation output
        </div>
        <label
          htmlFor="sim-import-file"
          className={`flex items-center justify-center gap-3 rounded-lg border-2 border-dashed py-8 cursor-pointer transition ${
            uploading
              ? "border-cyan-800 bg-cyan-950/30"
              : "border-slate-700 hover:border-cyan-700 hover:bg-cyan-950/20"
          }`}
          data-testid="sim-import-dropzone"
        >
          {uploading ? (
            <>
              <Loader2 className="h-5 w-5 text-cyan-400 animate-spin" />
              <span className="text-sm text-cyan-300">Parsing…</span>
            </>
          ) : (
            <>
              <Upload className="h-5 w-5 text-slate-400" />
              <span className="text-sm text-slate-300">
                Drop or click to select .xlsx / .csv / .xml / .json / .inp / .bkp
              </span>
            </>
          )}
          <input
            id="sim-import-file"
            ref={inputRef}
            type="file"
            className="hidden"
            accept=".xlsx,.xls,.csv,.xml,.json,.inp,.bkp"
            onChange={(e) => handleUpload(e.target.files?.[0])}
            data-testid="sim-import-file-input"
          />
        </label>
      </div>

      {/* Preview */}
      {loading && (
        <div className="text-xs text-slate-500 inline-flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading preview…
        </div>
      )}

      {preview && result && (
        <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-4 space-y-3"
             data-testid="sim-import-preview">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs font-semibold text-slate-300 uppercase tracking-wide">
              2 · Preview parsed data
            </div>
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-cyan-950/70 px-2 py-0.5 text-[10px] font-semibold text-cyan-300 uppercase tracking-wide">
                {result.simulator}
              </span>
              <span className="text-[10px] text-slate-500">
                {Math.round(result.confidence * 100)}% confidence · {preview.filename}
              </span>
              <button
                type="button"
                onClick={handleClear}
                className="text-slate-400 hover:text-rose-400 transition"
                title="Clear preview"
                data-testid="sim-import-clear-btn"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Completeness */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <StatChip label="Streams"      value={completeness.streams_count} accent="cyan" />
            <StatChip label="Equipment"    value={completeness.equipment_count} accent="cyan" />
            <StatChip label="Has T"        value={completeness.has_temperature} accent="slate" />
            <StatChip label="Has P"        value={completeness.has_pressure} accent="slate" />
            <StatChip label="Has flow"     value={completeness.has_flow} accent="slate" />
            <StatChip label="Composition" value={completeness.has_composition} accent="slate" />
            <StatChip label="Phase"        value={completeness.has_phase} accent="slate" />
            <StatChip
              label="PFD-ready"
              value={completeness.pfd_ready ? "Yes" : "No"}
              accent={completeness.pfd_ready ? "emerald" : "amber"}
            />
          </div>

          {/* Warnings */}
          {result.warnings?.length > 0 && (
            <div className="rounded border border-amber-900/40 bg-amber-950/20 p-2 space-y-1">
              {result.warnings.map((w, i) => (
                <div key={i} className="text-[11px] text-amber-200 inline-flex gap-1.5">
                  <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" /> {w}
                </div>
              ))}
            </div>
          )}

          {/* Streams table */}
          <SectionToggle
            label={`Streams (${result.streams?.length || 0})`}
            open={previewRows.streams}
            onToggle={() => setPreviewRows((s) => ({ ...s, streams: !s.streams }))}
            testid="toggle-streams"
          />
          {previewRows.streams && (
            <div className="overflow-x-auto rounded border border-slate-800">
              <table className="w-full text-xs" data-testid="sim-import-streams-table">
                <thead className="bg-slate-900/70 text-slate-400">
                  <tr>
                    <th className="text-left px-2 py-1.5">ID</th>
                    <th className="text-right px-2 py-1.5">T (°C)</th>
                    <th className="text-right px-2 py-1.5">P (barg)</th>
                    <th className="text-right px-2 py-1.5">Flow (kg/h)</th>
                    <th className="text-right px-2 py-1.5">Vap frac</th>
                    <th className="text-left px-2 py-1.5">Phase</th>
                  </tr>
                </thead>
                <tbody>
                  {(result.streams || []).slice(0, 40).map((s, i) => (
                    <tr key={i} className="border-t border-slate-800/70">
                      <td className="px-2 py-1 font-mono text-cyan-200">{s.id || s._stream_id || "—"}</td>
                      <td className="px-2 py-1 text-right text-slate-200">{fmt(s.temperature_c)}</td>
                      <td className="px-2 py-1 text-right text-slate-200">{fmt(s.pressure_barg)}</td>
                      <td className="px-2 py-1 text-right text-slate-200">{fmt(s.mass_flow_kgh, 0)}</td>
                      <td className="px-2 py-1 text-right text-slate-200">{fmt(s.vapor_fraction, 3)}</td>
                      <td className="px-2 py-1 text-slate-300">{s.phase || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {result.streams?.length > 40 && (
                <div className="text-[10px] text-slate-500 py-1 text-center bg-slate-900/40">
                  Showing first 40 of {result.streams.length} streams
                </div>
              )}
            </div>
          )}

          {/* Equipment */}
          {result.equipment?.length > 0 && (
            <>
              <SectionToggle
                label={`Equipment (${result.equipment.length})`}
                open={previewRows.equipment}
                onToggle={() => setPreviewRows((s) => ({ ...s, equipment: !s.equipment }))}
                testid="toggle-equipment"
              />
              {previewRows.equipment && (
                <div className="overflow-x-auto rounded border border-slate-800">
                  <table className="w-full text-xs" data-testid="sim-import-equipment-table">
                    <thead className="bg-slate-900/70 text-slate-400">
                      <tr>
                        <th className="text-left px-2 py-1.5">Tag</th>
                        <th className="text-left px-2 py-1.5">Type</th>
                        <th className="text-left px-2 py-1.5">Name</th>
                        <th className="text-right px-2 py-1.5">Duty (kW)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.equipment.map((e, i) => (
                        <tr key={i} className="border-t border-slate-800/70">
                          <td className="px-2 py-1 font-mono text-cyan-200">{e.tag}</td>
                          <td className="px-2 py-1 text-slate-300">{e.type}</td>
                          <td className="px-2 py-1 text-slate-400">{e.name}</td>
                          <td className="px-2 py-1 text-right text-slate-200">{fmt(e.duty_kw, 1)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {/* Commit */}
          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-800">
            <button
              type="button"
              onClick={handleCommit}
              disabled={committing || !completeness.streams_count}
              className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition ${
                committing || !completeness.streams_count
                  ? "bg-slate-800 text-slate-500 cursor-not-allowed"
                  : "bg-cyan-500 text-slate-950 hover:bg-cyan-400"
              }`}
              data-testid="sim-import-commit-btn"
            >
              {committing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Commit to Project PDO
            </button>
            <p className="text-[10px] text-slate-500 flex-1">
              Committing writes the parsed streams &amp; equipment into
              <code className="mx-1 text-slate-400">pdo.simulation</code>
              and marks B.1 complete so downstream stages unlock.
            </p>
          </div>
        </div>
      )}

      {err && (
        <div className="text-xs text-rose-300 inline-flex items-start gap-2 rounded border border-rose-900/40 bg-rose-950/30 px-3 py-2">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" /> {err}
        </div>
      )}
      {ok && (
        <div className="text-xs text-emerald-200 inline-flex items-start gap-2 rounded border border-emerald-900/40 bg-emerald-950/30 px-3 py-2"
             data-testid="sim-import-ok-banner">
          <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" /> {ok}
        </div>
      )}
    </div>
  );
}

function fmt(v, digits = 2) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
}

function StatChip({ label, value, accent = "slate" }) {
  const color = {
    cyan: "border-cyan-900/50 bg-cyan-950/30 text-cyan-200",
    emerald: "border-emerald-900/50 bg-emerald-950/30 text-emerald-200",
    amber: "border-amber-900/50 bg-amber-950/30 text-amber-200",
    slate: "border-slate-800 bg-slate-900/50 text-slate-200",
  }[accent];
  return (
    <div className={`rounded border px-2 py-1.5 ${color}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-sm font-semibold">{value ?? "—"}</div>
    </div>
  );
}

function SectionToggle({ label, open, onToggle, testid }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full text-left inline-flex items-center gap-2 text-xs font-semibold text-slate-300 hover:text-cyan-300 transition"
      data-testid={testid}
    >
      {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      {label}
      <Eye className="h-3.5 w-3.5 opacity-40" />
    </button>
  );
}
